import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { garagePinsApi } from '../../services/garagePinsApi';
import { GaragePinCandidate } from '../../types/garagePins';

interface GaragePinPickerProps {
  /** Garages already in the draft list — hidden from the results (the API only excludes saved pins). */
  excludeIds: string[];
  disabled: boolean;
  /** Shown under the box and as its tooltip when disabled, to say why. */
  disabledHint?: string;
  onPick: (candidate: GaragePinCandidate) => void;
}

const muted = { fontSize: '0.8125rem', color: 'var(--admin-text-muted)', marginTop: '0.5rem' };

/** Spec 061 Flow A step 2 — debounced (300 ms) search for an approved, unblocked garage to pin. */
export default function GaragePinPicker({ excludeIds, disabled, disabledHint, onPick }: GaragePinPickerProps) {
  const { token } = useAuth();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<GaragePinCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = term.trim();
    if (!q || !token) { setResults([]); setError(null); return; }
    let live = true;
    const t = window.setTimeout(async () => {
      setSearching(true);
      setError(null);
      try {
        const found = await garagePinsApi.candidates(q, token);
        if (live) setResults(found);
      } catch (err) {
        if (live) setError(err instanceof Error ? err.message : 'Garage search failed');
      } finally {
        if (live) setSearching(false);
      }
    }, 300);
    return () => { live = false; window.clearTimeout(t); };
  }, [term, token]);

  const visible = results.filter((c) => !excludeIds.includes(c.garageId));

  const pick = (c: GaragePinCandidate) => {
    onPick(c);
    setTerm('');
    setResults([]);
  };

  return (
    <div style={{ marginBottom: '1rem' }}>
      <input
        type="search"
        className="admin-search-input"
        style={{ width: '100%', boxSizing: 'border-box' }}
        placeholder="Add a garage…"
        aria-label="Add a garage"
        value={term}
        disabled={disabled}
        title={disabled ? disabledHint : undefined}
        onChange={(e) => setTerm(e.target.value)}
      />
      {disabled && disabledHint && <div style={muted}>{disabledHint}</div>}
      {!disabled && error && <div className="admin-info-banner danger" role="alert">{error}</div>}
      {!disabled && searching && <div style={muted}>Searching…</div>}
      {!disabled && !searching && term.trim() && visible.length === 0 && !error && (
        <div style={muted}>No garages found.</div>
      )}
      {!disabled && visible.length > 0 && (
        <ul className="promo-picker-results" aria-label="Garage results">
          {visible.map((c) => (
            <li key={c.garageId}>
              <div>
                <div style={{ fontWeight: 500 }}>{c.name}</div>
                {c.city && <div className="admin-text-muted" style={{ fontSize: '0.75rem' }}>{c.city}</div>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {c.serviceless && <span className="admin-badge warning">No bookable service</span>}
                <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => pick(c)}>
                  Add
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
